import { useEffect, useMemo, useState, useRef } from 'react';
import FullCalendar from "@fullcalendar/react";
import resourceTimelinePlugin from "@fullcalendar/resource-timeline";
import rrulePlugin from "@fullcalendar/rrule";
import momentTimezonePlugin from "@fullcalendar/moment-timezone";
import resourceTimeGridPlugin from "@fullcalendar/resource-timegrid";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from '@fullcalendar/interaction';

import AddAccount from "../components/AddAccount.jsx";
import GroupManager from "../components/GroupManager.jsx";
import CreateAppointment from "../components/CreateAppointment.jsx";
import { useConfig } from "../configContext.js";
import DatePicker from "react-datepicker";
import { registerLocale, setDefaultLocale } from "react-datepicker";
import fr from "date-fns/locale/fr";

import "react-datepicker/dist/react-datepicker.css";
import "../css/Home.css";
import ButtonsBar from "../components/ButtonsBar.jsx";
import CalendarSidebar from "../components/CalendarSidebar.jsx";
import Snackbar from "../components/Snackbar.jsx";

import { useAuth } from "../authContext.js";
import CustomEventContent from "../components/CustomEventContent.jsx";
import {
  PLANNING_DAY_VIEW, PLANNING_WEEK_VIEW, GROUPS_DAY_VIEW, GROUPS_WEEK_VIEW,
  isGroupFcView, isFlatGroupFcView, isTimelineFcView,
} from "../calendar/views.js";
import { useRowDensity } from "../calendar/density.js";
import { styleEvent } from "../calendar/eventStyle.js";
import { mergeGroupEvents } from "../calendar/mergeGroupEvents.js";
import { useHiddenMembers } from "../calendar/hiddenMembers.js";
import PlanningLegend from "../components/PlanningLegend.jsx";

registerLocale("fr", fr);
setDefaultLocale("fr");

export default function Home() {
  const [events, setEvents] = useState([]);
  const [hiddenDays] = useState([0, 6]);
  // The visible range, as FullCalendar reports it through datesSet. Null until
  // then: the fetch effects wait for it rather than guess a week and fetch
  // twice on mount.
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  // Bumped after a change the grid cannot see (an appointment was created), to
  // make the fetch effects run again for the same range.
  const [refreshTick, setRefreshTick] = useState(0);
  const [showAddAccountModal, setShowAddAccountModal] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [notHideAccounts, setNotHideAccounts] = useState([]);

  const [groups, setGroups] = useState([]);
  const [showGroupManagerModal, setShowGroupManagerModal] = useState(false);
  const [showCreateAppointmentModal, setShowCreateAppointmentModal] = useState(false);
  const [selectionInfo, setSelectionInfo] = useState(null);
  const [currentView, setCurrentView] = useState(PLANNING_WEEK_VIEW);

  const [showDatePicker, setShowDatePicker] = useState(false);
  const [datePickerTop, setDatePickerTop] = useState(0);
  const [datePickerRight, setDatePickerRight] = useState(0);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const datePickerRef = useRef(null);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [color, setColor] = useState('success');
  const [message, setMessage] = useState('');

  const calendarRef = useRef(null);
  const calendarContainerRef = useRef(null);

  // Shift+clic multi-sélection
  const shiftPressedRef = useRef(false);
  const pendingAttendeesRef = useRef([]);
  const pendingTimeRef = useRef({ start: null, end: null });
  const [ctrlActive, setCtrlActive] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [shiftHighlights, setShiftHighlights] = useState([]);

  const { apiUrl } = useConfig();
  const { user, loadingInitial } = useAuth();
  const { density, setDensity } = useRowDensity();
  const { hidden: hiddenMembers, toggleMember, setGroupHidden } = useHiddenMembers();


  const updatePosition = (event) => {
    const mouseX = event.clientX; // Position X de la souris (par rapport à la fenêtre)
    const mouseY = event.clientY; // Position Y de la souris
    
    setDatePickerTop(mouseY);
    setDatePickerRight(window.innerWidth - mouseX); // Calcul de "right" en fonction de la largeur de l'écran
  };

  // Shift key tracking pour multi-sélection : keyup ouvre la modale quand on relâche Shift
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Shift') {
        shiftPressedRef.current = true;
        setCtrlActive(true);
      }
    };
    const handleKeyUp = (e) => {
      if (e.key === 'Shift') {
        shiftPressedRef.current = false;
        setCtrlActive(false);
        // Retirer tous les highlights (lignes + cellules)
        document.querySelectorAll('.resource-selected').forEach(el => el.classList.remove('resource-selected'));
        setShiftHighlights([]);
        if (pendingAttendeesRef.current.length > 0) {
          const attendees = [...pendingAttendeesRef.current];
          const time = { ...pendingTimeRef.current };
          pendingAttendeesRef.current = [];
          pendingTimeRef.current = { start: null, end: null };
          setPendingCount(0);

          setSelectionInfo({
            start: time.start,
            end: time.end,
            resourceEmail: null,
            resourceUuid: null,
            groupMembers: null,
            groupName: null,
            prefilledAttendees: attendees,
          });
          setShowCreateAppointmentModal(true);
        }
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('keyup', handleKeyUp);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (datePickerRef.current && !datePickerRef.current.contains(event.target)) {
        setShowDatePicker(false);  // Fermer le DatePicker
      }
    };

    // Écouter les clics globaux
    document.addEventListener('mousedown', handleClickOutside);
    
    // Nettoyage du listener à la fin
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // The range and the followed accounts drive the fetch; datesSet and the
  // account list only update those. One place fetches, its dependencies are
  // the real ones, and a slow answer to a range we have left cannot overwrite
  // the current one.
  useEffect(() => {
    if (!startDate || !endDate || accounts.length === 0) return undefined;
    let cancelled = false;
    (async () => {
      let data;
      try {
        const response = await fetch(`${apiUrl}/calendars`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ start: startDate, end: endDate }),
        });
        data = await response.json();
      } catch (error) {
        if (cancelled) return;
        if (error && error.code === 401) {
          window.location.href = `${apiUrl}/logout`;
        }
        setMessage(error.message);
        setColor("danger");
        setSnackbarOpen(true);
        return;
      }
      if (cancelled) return;
      if (data.success === false) {
        setMessage(data.message);
        setColor("danger");
        setSnackbarOpen(true);
      }
      setEvents(data.events);
    })();
    return () => {
      cancelled = true;
    };
  }, [apiUrl, startDate, endDate, accounts, refreshTick]);



  const resourceDidMount = (info) => {
      info.el.style.whiteSpace = "normal";
      info.el.setAttribute("title", info.resource?.title || "name");
  }
  // Accounts and groups for the signed-in user. Both dependencies are stable
  // for the life of the page (runtime config, current session), so listing
  // them costs nothing and keeps the effect honest; the cancelled flag keeps
  // a late answer from writing into an unmounted page.
  useEffect(() => {
    let cancelled = false;
    fetch(`${apiUrl}/accounts/${user.username}`, { credentials: "include" })
      .then((response) => response.json())
      .then((data) => {
        if (cancelled) return;
        setAccounts(Object.values(data.accounts));
        setNotHideAccounts(Object.values(data.not_hide_accounts));
      });
    fetch(`${apiUrl}/groups`, { credentials: "include" })
      .then((response) => response.json())
      .then((data) => {
        if (!cancelled && data.success) setGroups(data.groups);
      });
    return () => {
      cancelled = true;
    };
  }, [apiUrl, user.username]);



  // The answer on hand, tagged with the range and groups it answers. Whether
  // the view is "loading" is derived from that tag rather than set by hand —
  // a flag toggled synchronously inside an effect cascades renders.
  const [groupEventsResult, setGroupEventsResult] = useState({ key: null, events: [] });
  // Dedup and 60s TTL for in-flight and recent fetches; a ref, since it must
  // be read and written by concurrent calls without re-rendering.
  const groupEventsCacheRef = useRef({ start: null, end: null, groupIds: null, fetchedAt: 0 });

  const isGroupView = isGroupFcView(currentView);
  // Week Groups: a plain hour grid, so no resource row carries a member name.
  const isFlatGroupView = isFlatGroupFcView(currentView);
  // Day Groups keeps the horizontal timeline: its resource column carries the
  // collapsible hierarchy, and a drag on a group row invites every member.
  const isTimelineView = isTimelineFcView(currentView);

  const getGroupCacheKey = () => groups.map(g => g.id).sort().join(',');
  const groupResultKey = (start, end) => `${start}|${end}|${getGroupCacheKey()}`;

  const wantedGroupKey = groupResultKey(startDate, endDate);
  const groupEvents = groupEventsResult.key === wantedGroupKey ? groupEventsResult.events : [];
  const groupEventsLoading = isGroupView && groups.length > 0 && groupEventsResult.key !== wantedGroupKey;

  // Group events, fetched by the effect that depends on what they answer to:
  // the view, the groups and the range. The ref dedups concurrent runs and
  // keeps a 60s TTL, so flipping between the two Groups views does not hit
  // Zimbra twice for the same week.
  useEffect(() => {
    if (!isGroupView || groups.length === 0 || !startDate || !endDate) return undefined;
    const groupIds = groups.map(g => g.id).sort().join(',');
    const cache = groupEventsCacheRef.current;
    const fresh = cache.start === startDate && cache.end === endDate
      && cache.groupIds === groupIds && Date.now() - cache.fetchedAt < 60000;
    if (fresh) return undefined;

    groupEventsCacheRef.current = { start: startDate, end: endDate, groupIds, fetchedAt: Date.now() };
    let cancelled = false;
    let settled = false;
    (async () => {
      try {
        const response = await fetch(`${apiUrl}/groups/events`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ start: startDate, end: endDate }),
        });
        const data = await response.json();
        if (cancelled) return;
        if (data.success) {
          setGroupEventsResult({ key: `${startDate}|${endDate}|${groupIds}`, events: data.events });
        }
      } catch (error) {
        console.error("Error fetching group events:", error);
        // Let a later run retry rather than trust a failed fetch for 60s.
        groupEventsCacheRef.current = { start: null, end: null, groupIds: null, fetchedAt: 0 };
      } finally {
        settled = true;
      }
    })();
    return () => {
      cancelled = true;
      // A run cancelled mid-flight never delivers: forget it, or an identical
      // key within the TTL would be skipped and the view would load forever.
      if (!settled) groupEventsCacheRef.current = { start: null, end: null, groupIds: null, fetchedAt: 0 };
    };
  }, [apiUrl, isGroupView, groups, startDate, endDate, refreshTick]);

  const getResourcesForGroupView = () => {
    return groups.map(group => ({
      id: `group_${group.id}`,
      title: group.name,
      isGroupResource: true,
      groupId: group.id,
      children: (group.members || [])
        .filter(member => !hiddenMembers.has(member.account_uuid))
        .map(member => ({
          id: `${group.id}_${member.account_uuid}`,
          title: member.account_name,
          uuid: member.account_uuid,
          name: member.account_name,
        })),
    }));
  };

  const getEventsForGroupView = () => {
    // Remap events: resourceId → groupId_resourceId. memberUuid keeps the
    // original account, which the flat week grid needs to name its owner.
    return groupEvents
      .filter(event => !hiddenMembers.has(event.resourceId))
      .map(event => ({
        ...event,
        memberUuid: event.resourceId,
        resourceId: `${event.groupId}_${event.resourceId}`,
      }));
  };

  const accountColorById = useMemo(() => {
    const byId = new Map();
    for (const account of accounts) byId.set(account.uuid, account.color);
    return byId;
  }, [accounts]);

  const styledEvents = useMemo(
    () => events.map((event) => styleEvent(event, accountColorById.get(event.resourceId))),
    [events, accountColorById],
  );

  const memberNameByUuid = useMemo(() => {
    const byUuid = new Map();
    for (const group of groups) {
      for (const member of group.members || []) {
        byUuid.set(member.account_uuid, member.account_name);
      }
    }
    return byUuid;
  }, [groups]);

  const styledGroupEvents = useMemo(() => {
    const events = getEventsForGroupView();
    if (!isFlatGroupView) {
      return events.map((event) => styleEvent(event, event.color));
    }
    // One block per slot rather than one per member: ninety-seven members
    // otherwise stack ninety-seven slivers over the same hour.
    return mergeGroupEvents(events, memberNameByUuid).map((event) => styleEvent(event, event.color, {
      // Nothing else names the person here, so a single-member block gets a
      // vivid fill and their name in the title.
      solidFill: true,
      owner: event.memberCount === 1 ? event.members[0] : undefined,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupEvents, isFlatGroupView, memberNameByUuid, hiddenMembers]);

  // FullCalendar measures its container once and does not watch it: folding the
  // sidebar left the grid at its previous width, with a blank band down the
  // right-hand side. Watch the container instead of wiring this to the one
  // action that triggers it — a window resize or a zoom needs the same thing.
  useEffect(() => {
    const container = calendarContainerRef.current;
    if (!container || typeof ResizeObserver === "undefined") return undefined;

    let frame = 0;
    const observer = new ResizeObserver(() => {
      // On the next frame: updateSize() itself writes layout, and calling it
      // from inside the callback makes the observer fire again.
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => calendarRef.current?.getApi()?.updateSize());
    });
    observer.observe(container);
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, []);

  const goToDate = (date) => {
    if (calendarRef.current) {
      const calendarApi = calendarRef.current.getApi();
      calendarApi.gotoDate(date);
    }
  };

  const handleSelect = (info) => {
    const resource = info.resource;
    const isShift = shiftPressedRef.current || info.jsEvent?.shiftKey || false;
    let resourceEmail = null;
    let resourceUuid = null;

    // The flat week Groups grid has no resource rows, so there is no group row
    // to drag on — the way a whole group used to be invited. Selecting a slot
    // there invites everyone the view is showing instead: the groups ticked in
    // the sidebar, minus the members unticked. The dialog lists them as chips,
    // so the selection can be trimmed before sending.
    if (isFlatGroupView && !resource) {
      const invitedGroups = groups
        .map((group) => ({
          id: group.id,
          name: group.name,
          members: (group.members || [])
            .filter((m) => !hiddenMembers.has(m.account_uuid))
            .map((m) => ({ name: m.account_name, uuid: m.account_uuid })),
        }))
        .filter((group) => group.members.length > 0);
      if (invitedGroups.length === 0) return;
      setSelectionInfo({
        start: info.start,
        end: info.end,
        resourceEmail: null,
        resourceUuid: null,
        // Structured rather than flattened: the dialog shows them as a tree,
        // because ninety-seven chips in a row cannot be read or trimmed.
        invitedGroups,
        prefilledAttendees: [],
      });
      setShowCreateAppointmentModal(true);
      return;
    }

    if (resource) {
      const resourceId = resource.id;

      // Cas 1 : Clic sur la ligne du groupe (resource parent) → inviter tous les membres
      if (isGroupView && resourceId.startsWith('group_')) {
        const groupId = resourceId.replace('group_', '');
        const group = groups.find(g => String(g.id) === String(groupId));
        if (group) {
          const members = (group.members || [])
            .filter(m => !hiddenMembers.has(m.account_uuid))
            .map(m => ({ name: m.account_name, uuid: m.account_uuid }));
          setSelectionInfo({
            start: info.start,
            end: info.end,
            resourceEmail: null,
            resourceUuid: null,
            invitedGroups: [{ id: group.id, name: group.name, members }],
            prefilledAttendees: [],
          });
          setShowCreateAppointmentModal(true);
        }
        return;
      }

      // Ressource membre (vue groupe ou vue normale)
      if (isGroupView && resourceId.includes('_')) {
        const parts = resourceId.split('_');
        resourceUuid = parts.slice(1).join('_');
        resourceEmail = resource.extendedProps?.name || resource.title;
      } else {
        resourceUuid = resourceId;
        resourceEmail = resource.extendedProps?.name || resource.title;
      }
    }

    // Cas 2 : Shift+clic → accumuler/désélectionner les invités sans ouvrir la modale
    if (isShift && resourceEmail) {
      if (!pendingTimeRef.current.start) {
        pendingTimeRef.current = { start: info.start, end: info.end };
      }
      const idx = pendingAttendeesRef.current.findIndex(a => a.name === resourceEmail);
      const resourceId = resource?.id;
      if (idx !== -1) {
        // Désélection
        pendingAttendeesRef.current.splice(idx, 1);
        // Retirer les highlights (ligne + cellule)
        if (resourceId) {
          document.querySelectorAll(`[data-resource-id="${resourceId}"]`).forEach(el => el.classList.remove('resource-selected'));
          setShiftHighlights(prev => prev.filter(e => e.resourceId !== resourceId));
        }
      } else {
        // Sélection
        pendingAttendeesRef.current.push({ name: resourceEmail, uuid: resourceUuid });
        // Ajouter le highlight sur la ligne + cellule
        if (resourceId) {
          document.querySelectorAll(`[data-resource-id="${resourceId}"]`).forEach(el => el.classList.add('resource-selected'));
          setShiftHighlights(prev => [...prev, {
            id: `shift_${resourceId}`,
            start: info.start,
            end: info.end,
            resourceId: resourceId,
            display: 'background',
            color: 'rgba(25, 118, 210, 0.35)',
          }]);
        }
      }
      setPendingCount(pendingAttendeesRef.current.length);
      setCtrlActive(true);
      return;
    }

    // Cas 3 : Clic simple → inviter la personne cliquée
    setSelectionInfo({
      start: info.start,
      end: info.end,
      resourceEmail,
      resourceUuid,
      groupMembers: null,
      groupName: null,
      prefilledAttendees: resourceEmail ? [{ name: resourceEmail, uuid: resourceUuid }] : [],
    });
    setShowCreateAppointmentModal(true);
  };

  return (!loadingInitial &&
    <div className="flex h-screen w-full flex-col bg-gray-50">
      {/* Barre de boutons */}
      <ButtonsBar
        setShowAddAccountModal={setShowAddAccountModal}
        setShowGroupManagerModal={setShowGroupManagerModal}
        setShowDatePicker={setShowDatePicker}
        density={density}
        setDensity={setDensity}
        updatePosition={updatePosition}
        isGroupView={isGroupView}
        ctrlActive={ctrlActive}
        pendingCount={pendingCount}
      />

      {/* Overlay du DatePicker */}
      {showDatePicker && (
        <div
          ref={datePickerRef}
          className="absolute z-20 rounded-md border border-gray-200 bg-white p-3 shadow-lg"
          style={{ top: `${datePickerTop}px`, right: `${datePickerRight}px` }}
        >
          <DatePicker 
            inline 
            onChange={(date) => { goToDate(date); setSelectedDate(date); setShowDatePicker(false); }} 
            dateFormat="dd/MM/yyyy" 
            selected={selectedDate} 
            closeOnScroll={true}
          />
        </div>
      )}

      {/* Modales */}
      {showAddAccountModal && (
        <AddAccount
          startDate={startDate}
          endDate={endDate}
          setAccounts={setAccounts}
          setNotHideAccounts={setNotHideAccounts}
          setColor={setColor}
          setMessage={setMessage}
          setSnackbarOpen={setSnackbarOpen}
          onClose={() => setShowAddAccountModal(false)}
          accounts={accounts}
        />
      )}
      {showGroupManagerModal && (
        <GroupManager
          onClose={() => setShowGroupManagerModal(false)}
          groups={groups}
          setGroups={setGroups}
          setMessage={setMessage}
          setColor={setColor}
          setSnackbarOpen={setSnackbarOpen}
        />
      )}
      {showCreateAppointmentModal && (
        <CreateAppointment

          onClose={() => setShowCreateAppointmentModal(false)}
          selectionInfo={selectionInfo}
          setMessage={setMessage}
          setColor={setColor}
          setSnackbarOpen={setSnackbarOpen}
          onSuccess={() => {
            // The new appointment is on the server, not in the grid: forget
            // the cached group answer and make both fetch effects run again.
            groupEventsCacheRef.current = { start: null, end: null, groupIds: null, fetchedAt: 0 };
            setRefreshTick((tick) => tick + 1);
          }}
        />
      )}

      {/* Sidebar + calendrier */}
      <div className="flex min-h-0 flex-1">
        <CalendarSidebar
          accounts={accounts}
          setAccounts={setAccounts}
          setNotHideAccounts={setNotHideAccounts}
          groups={groups}
          setGroups={setGroups}
          isGroupView={isGroupView}
          onAddAccount={() => setShowAddAccountModal(true)}
          onManageGroups={() => setShowGroupManagerModal(true)}
          hiddenMembers={hiddenMembers}
          onToggleMember={toggleMember}
          onSetGroupHidden={setGroupHidden}
          setMessage={setMessage}
          setColor={setColor}
          setSnackbarOpen={setSnackbarOpen}
        />
      <div
        ref={calendarContainerRef}
        className="relative flex min-h-0 flex-1 flex-col overflow-y-auto"
      >
        {isGroupView && groupEventsLoading && (
          <div
            role="status"
            aria-live="polite"
            className="absolute inset-x-0 bottom-0 top-[50px] z-10 flex items-center justify-center bg-white/80"
          >
            <div className="flex flex-col items-center gap-3">
              <span className="h-9 w-9 animate-spin rounded-full border-4 border-gray-200 border-t-brand-500" />
              <p className="text-gray-700">Chargement des emplois du temps…</p>
            </div>
          </div>
        )}

        <FullCalendar
          plugins={[
            resourceTimeGridPlugin,
            resourceTimelinePlugin,
            rrulePlugin,
            momentTimezonePlugin,
            dayGridPlugin,
            interactionPlugin
          ]}
          ref={calendarRef}
          // The density is part of the calendar's identity: changing it
          // remounts FullCalendar. updateSize() is not enough — it re-measures
          // the grid but leaves every block at the offsets computed for the
          // previous slot height, so the whole grid rendered one step behind
          // (switching to Compacte laid the blocks out for Normale, up to 64px
          // off their hour line). The cost is a refetch, which changing the
          // density rarely does twice in a row.
          key={density}
          resourceAreaWidth={isTimelineView ? "15%" : undefined}
          schedulerLicenseKey="GPL-My-Project-Is-Open-Source"
          initialView={PLANNING_WEEK_VIEW}
          locale="fr"

          resources={
            isFlatGroupView ? []
              : isGroupView ? (groupEventsLoading ? [] : getResourcesForGroupView())
                : notHideAccounts
          }
          resourceOrder={"type1"}
          resourceLabelDidMount={resourceDidMount}
          resourceLabelContent={(arg) => (
              <div style={{
                fontSize: "13px",
                fontWeight: "bold",
                overflowWrap: "anywhere",
              }} title={arg.resource._resource.extendedProps.name || arg.resource.title}>
                  {arg.resource.title}
              </div>
          )}
          dayHeaderFormat={{
            weekday: "short",
          }}
          navLinks={true}
          navLinkDayClick={(date) => {
            const calendarApi = calendarRef.current.getApi();
            calendarApi.changeView(PLANNING_DAY_VIEW, date);
            calendarApi.gotoDate(date);
          }}
          selectable={true}
          selectAllow={(selectInfo) => {
            // En mode Shift avec un créneau déjà choisi, verrouiller sur le même horaire
            if (shiftPressedRef.current && pendingTimeRef.current.start) {
              const locked = pendingTimeRef.current;
              return selectInfo.start.getTime() === locked.start.getTime() &&
                     selectInfo.end.getTime() === locked.end.getTime();
            }
            return true;
          }}
          select={handleSelect}
          eventDisplay="block"
          eventContent={(info) => <CustomEventContent info={info} />}
          events={[...(isGroupView ? styledGroupEvents : styledEvents), ...shiftHighlights]}
          nowIndicator={true}
          businessHours={true}
          timeZone="Europe/Paris"
          hiddenDays={hiddenDays}
          allDaySlot={false}
          datesSet={(info) => {
            // Not viewDidMount: the Jour and Groupe/Jour views are both
            // resourceTimeline, so switching between them reuses the mounted
            // view and never fires it — currentView stayed stale and the grid
            // kept the other view's resources. datesSet fires on every view
            // and date change.
            setCurrentView(info.view.type);
            // The visible range is state because a change of accounts or
            // groups has to refetch it; set here, in the event handler, not
            // inside the fetch helpers.
            setStartDate(info.startStr);
            setEndDate(info.endStr);
          }}
          height="auto"
          scrollTime="7:00:00"
          headerToolbar={{
            start: "prev,next today",
            center: "title",
            end: `${PLANNING_DAY_VIEW},${PLANNING_WEEK_VIEW},${GROUPS_DAY_VIEW},${GROUPS_WEEK_VIEW}`,
          }}
          views={{
            // Accounts over a day: one column per calendar.
            [PLANNING_DAY_VIEW]: {
              slotMinTime: "07:00:00",
              slotMaxTime: "20:00:00",
            },
            [PLANNING_WEEK_VIEW]: {
              slotMinTime: "07:00:00",
              slotMaxTime: "20:00:00",
              // No expandRows here: stretching the rows to fill the pane
              // overrides the slot height, and the density choice (compact /
              // normal / comfortable) then did nothing at all.
            },
            // Groups over a day: rows, so the hierarchy and its "invite the
            // whole group" row survive.
            [GROUPS_DAY_VIEW]: {
              slotMinTime: "07:00:00",
              slotMaxTime: "20:00:00",
              resourceAreaHeaderContent: "Groupes",
            },
            // Groups over a week: members are not columns at all — seven days
            // of them would not fit. Everyone shares the day column and the
            // events are merged per slot.
            [GROUPS_WEEK_VIEW]: {
              type: "timeGrid",
              duration: { weeks: 1 },
              slotMinTime: "07:00:00",
              slotMaxTime: "20:00:00",
            },
          }}
          buttonText={{
            [PLANNING_DAY_VIEW]: "Jour",
            [PLANNING_WEEK_VIEW]: "Semaine",
            [GROUPS_DAY_VIEW]: "Groupe/Jour",
            [GROUPS_WEEK_VIEW]: "Groupe/Semaine",
            today: "Aujourd'hui",
          }}
        />
        <PlanningLegend />
      </div>
      </div>
      <Snackbar
        open={snackbarOpen}
        message={message}
        color={color}
        onClose={() => setSnackbarOpen(false)}
      />
    </div>
  );
}